import React, {
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';

import { omitBy, isNil, sortBy, reverse } from 'lodash-es';

import LoadingScreen from 'components/molecules/LoadingScreen';
import useAuthentication from 'hooks/common/useAuthentication';
import useError, {
  buildErrorResponse,
  ErrorResponse,
} from 'hooks/common/useError';
import useReactQuery from 'hooks/common/useReactQuery';
import { ParentWithStudents } from 'interfaces/parent';
import { AppContext } from 'providers/ContextProvider';

import InternalServerError from '../errors/InternalServerError';

const AutoLogin: React.FC<PropsWithChildren> = ({ children }) => {
  const { isLoggedIn, refreshAccessToken, login } = useAuthentication();
  const { unauthLogout } = useError();
  const { useQuery } = useReactQuery();
  const { currentUser, setCurrentUser } = useContext(AppContext);
  const [isLoadingAccessToken, setIsLoadingAccessToken] =
    useState<boolean>(true);
  const [isErrorAccessToken, setIsErrorAccessToken] = useState<boolean>(false);
  useEffect(() => {
    setIsLoadingAccessToken(true);
    void refreshAccessToken()
      .catch((e) => {
        if ((e as ErrorResponse).status === 401) {
          return;
        }
        setIsErrorAccessToken(true);
      })
      .finally(() => {
        setIsLoadingAccessToken(false);
      });
  }, [login, refreshAccessToken]);

  const { data, error, isSuccess } = useQuery<ParentWithStudents>({
    queryKey: ['me'],
    url: '/api/school/parents/me',
    enabled: !isLoadingAccessToken && isLoggedIn && !currentUser,
  });

  useEffect(() => {
    if (isSuccess && data) {
      setCurrentUser(data);
    }
  }, [isSuccess, data, setCurrentUser]);

  const isLoading =
    isLoadingAccessToken ||
    (!isLoadingAccessToken && isLoggedIn && !currentUser);

  // show error page when API responds error except 401
  useEffect(() => {
    if (error) {
      const statuses = omitBy({ error }, isNil);

      reverse(sortBy(Object.values(statuses), ['status'])).map((value) => {
        if (value?.status !== 401) {
          throw buildErrorResponse(value || {});
        }
      });
    }
  }, [unauthLogout, error]);

  if (isErrorAccessToken) {
    return <InternalServerError isLoggedIn={false} />;
  }

  if (isLoading) {
    return <LoadingScreen />;
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

export default AutoLogin;
